import {ElementCursor, ElementProperties, ElementView} from '@app/modules/svg/element/ElementView';
import {ElementType} from '@app/modules/svg/dataSource/constant/ElementType';
import {Container} from '@app/modules/svg/Container';
import {Point} from '@app/modules/svg/model/Point';
import {NodeEditable} from '@app/modules/svg/element/shape/pointed/NodeEditable';
import {MoveDrawable} from '@app/modules/svg/service/tool/draw/type/MoveDrawable';
import {LineView} from '@app/modules/svg/element/shape/pointed/LineView';
import { Angle } from '@app/modules/svg/public-api';

export class SegmentCursor extends ElementCursor {}

export enum PointType {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  ARROW = 'ARROW',
}

export interface SegmentProperties extends ElementProperties {
  startPointType: PointType;
  endPointType: PointType;
}

export class SegmentView extends LineView implements NodeEditable, MoveDrawable {
  protected static readonly POINT_RADIUS: number = 8;

  protected override svgElement: SVGGElement = document.createElementNS(ElementView.svgURI, 'g');
  protected svgLineElement: SVGLineElement = document.createElementNS(ElementView.svgURI, 'line');

  protected override _type: ElementType = ElementType.SEGMENT;

  constructor(container: Container,
              properties: SegmentProperties = { startPointType: PointType.OPEN, endPointType: PointType.OPEN },
              startPoint: Point = { x: 0, y: 0 },
              endPoint: Point = { x: 0, y: 0 },
              ownerId?: string,
              index?: number
  ) {
    super(
      container,
      {},
      startPoint,
      endPoint,
      ownerId,
      index
    );

    this.setProperties(properties);

    this.__updateView__();
  }

  public togglePointType(pointIndex: number): void {
    const properties = this._properties as SegmentProperties;

    if (pointIndex === 0) {
      properties.startPointType =
        properties.startPointType === PointType.OPEN ? PointType.CLOSED :
          properties.startPointType === PointType.CLOSED ? PointType.OPEN :
            PointType.ARROW;
    } else if (pointIndex === 1) {
      properties.endPointType =
        properties.endPointType === PointType.OPEN ? PointType.CLOSED :
          properties.endPointType === PointType.CLOSED ? PointType.OPEN :
            PointType.ARROW;
    }

    this.__updateView__();
  }

  public override __updateView__(): void {
    if (!this._points[0] || !this._points[1] || !this.svgLineElement) {
      return;
    }

    const properties = this._properties as SegmentProperties;

    this.svgElement.innerHTML = '';

    this.setAttr({
      x1: this._points[0].x,
      y1: this._points[0].y,
      x2: this._points[1].x,
      y2: this._points[1].y
    }, this.svgLineElement);

    const endPointRotation = -Angle.fromTwoPoints(this._points[0], this._points[1]);

    const svgStartPointElement = this.createPointElement(properties.startPointType, this._points[0], 0);
    const svgEndPointElement = this.createPointElement(properties.endPointType, this._points[1], endPointRotation);

    this.svgElement.appendChild(this.svgLineElement);
    this.svgElement.appendChild(svgStartPointElement);
    this.svgElement.appendChild(svgEndPointElement);
  }

  private createPointElement(pointType: PointType, point: Point, angle: number): SVGElement {
    switch (pointType) {
      case PointType.OPEN:
      case PointType.CLOSED: {
        const svgPointElement = document.createElementNS(ElementView.svgURI, 'circle');
        const pointFill = pointType === PointType.OPEN ? '#fff' : '#000';

        this.setAttr({
          'stroke-width': 1,
          stroke: '#000',
          fill: pointFill,
          r: SegmentView.POINT_RADIUS,
          cx: point.x,
          cy: point.y,
        }, svgPointElement);

        return svgPointElement;
      }
      case PointType.ARROW: {
        const svgPointMarker = document.createElementNS(ElementView.svgURI, 'marker');
        svgPointMarker.id = this.id + '-arrow';

        const svgArrowElement = document.createElementNS(ElementView.svgURI, 'polygon');

        const x = point.x;
        const y = point.y;

        this.setAttr({
          points: `${x},${y-5} ${x+10},${y} ${x},${y+5}`,
          'end-marker': `url(#${ svgPointMarker.id })`,
          fill: '#000',
        }, svgArrowElement);

        svgArrowElement.style.transform = `rotate(${angle}deg)`;
        svgArrowElement.style.transformOrigin = `${point.x}px ${point.y}px`;

        svgPointMarker.appendChild(svgArrowElement);

        return svgArrowElement;
      }
    }
  }
}
